/**
 * Block Layout Component
 * Common layout for all block components (Apps, Publications, etc.)
 * Handles title, horizontal scroll, and loading/error states
 */

'use client';

import { buttonVariants } from '@/components/ui/Button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import type { ThemeBadgeTheme } from '@uicwebmaster/taxonomy-themes/components';
import { Asterisk } from 'lucide-react';
import Link from 'next/link';
import React from 'react';
import CustomAlert from '../auth/CustomAlert';

interface BlockLayoutProps<T> {
  /** Title to display */
  title: string;
  /** Array of data to paginate */
  data: T[];
  /** URL to redirect to when the "See All" button is clicked */
  seeAllUrl?: string;
  /** Function to render each item */
  renderItem: (item: T) => React.ReactNode;
  /** Loading state */
  loading: boolean;
  /** Error message (null if no error) */
  error: string | null;
  /** Whether to show empty state (if false, returns null when empty) */
  showEmptyState?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Array of theme IDs or theme objects to display as badges */
  themes?: string[] | ThemeBadgeTheme[];
  /** Array of theme IDs to display as badges */
  themesIds?: string[];
  /** Maximum number of theme badges to show before overflow */
  maxVisibleThemes?: number;
  /** Whether to show the tag icon next to badges */
  showThemeIcon?: boolean;
  /** Width of each item in pixels (default: 280px) */
  itemWidth?: number;
  /** Edit button to display */
  editButton?: React.ReactNode;
}

export function BlockLayout<T>({
  title,
  data,
  seeAllUrl,
  renderItem,
  loading,
  error,
  showEmptyState = false,
  emptyMessage = 'No items available.',
  themes,
  themesIds,
  maxVisibleThemes = 6,
  showThemeIcon = true,
  itemWidth = 280,
  editButton,
}: BlockLayoutProps<T>) {
  if (loading) {
    return <Skeleton className="h-48 w-full bg-slate-500/10" />;
  }

  if (error) {
    return null; // Silently fail - don&apos;t show error on home page
  }

  // Check if data is empty or undefined
  const isEmpty = !data || data.length === 0;

  if (isEmpty && !showEmptyState) {
    return null; // Don&apos;t show empty state
  }

  if (isEmpty && showEmptyState) {
    return <CustomAlert variant="info" title={title} description={<p>{emptyMessage}</p>} />;
  }

  return (
    <div className="space-y-4 p-4 lg:px-6 bg-slate-500/5 rounded">
      <div className="space-y-2">
        <div className="flex gap-2  justify-between items-center">
          <h3 className="opacity-80">{title}</h3>

          {seeAllUrl && (
            <Link
              href={seeAllUrl}
              className={buttonVariants({ variant: 'ghost' }) + ' text-xs text-muted-foreground'}
            >
              see all
              <Asterisk className="size-4" />
            </Link>
          )}
        </div>
      </div>
      <ScrollArea className="w-full" orientation="horizontal">
        <div className="flex gap-6 pb-4" style={{ width: 'max-content' }}>
          {data.map((item, index) => {
            // Extract key from item if it has an id property, otherwise use index
            let key: React.Key = index;
            if (item && typeof item === 'object' && 'id' in item) {
              const itemId = (item as { id: React.Key }).id;
              if (itemId != null) {
                key = itemId;
              }
            }
            return (
              <div key={key} style={{ width: `${itemWidth}px` }}>
                {renderItem(item)}
              </div>
            );
          })}
        </div>
      </ScrollArea>
      <div className="flex items-center justify-end gap-4">
      {editButton && <div className="text-xs text-muted-foreground flex items-center gap-1">{editButton}</div>}
      
      </div>
    </div>
  );
}
